import { useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Plus,
  Trash2,
  Loader2,
  ShoppingCart,
  Package,
  Building2,
  Calendar,
  FileText,
  Calculator,
} from 'lucide-react';
import { format, addDays } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Combobox } from '@/components/ui/combobox';
import { FileUpload } from '@/components/ui/file-upload';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';

interface OrderItemForm {
  productId: string;
  productName?: string;
  productCode?: string;
  productDescription?: string;
  quantity: number;
  unitPrice: number;
  taxRate: number;
  notes?: string;
}

interface PurchaseOrderForm {
  supplierId: string;
  warehouseId: string;
  orderDate: string;
  expectedDate?: string;
  currency: string;
  paymentTerms?: string;
  deliveryTerms?: string;
  notes?: string;
  items: OrderItemForm[];
}

interface SupplierProduct {
  id: string;
  productId: string;
  unitCost: number;
  currency: string;
}

export function NewPurchaseOrderPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceFile, setInvoiceFile] = useState<{ fileUrl: string; fileKey: string } | null>(null);

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
  } = useForm<PurchaseOrderForm>({
    defaultValues: {
      orderDate: format(new Date(), 'yyyy-MM-dd'),
      expectedDate: format(addDays(new Date(), 14), 'yyyy-MM-dd'),
      currency: 'EUR',
      items: [],
    },
  });

  const { fields: items, append: appendItem, remove: removeItem } = useFieldArray({
    control,
    name: 'items',
  });

  const watchedValues = watch();
  const selectedSupplierId = watchedValues.supplierId;
  const selectedWarehouseId = watchedValues.warehouseId;

  // Queries
  const { data: suppliersData } = trpc.tradingSupplier.list.useQuery({});
  const { data: warehousesData } = trpc.warehouse.list.useQuery({});
  const { data: productsData } = trpc.tradingProduct.list.useQuery(
    { supplierId: selectedSupplierId || undefined, limit: 1000 },
    { enabled: !!selectedSupplierId }
  );

  const suppliers = suppliersData?.suppliers ?? [];
  const warehouses = warehousesData ?? [];
  const products = productsData?.products ?? [];

  // Build product options for the combobox (description shown + searchable via keywords)
  const productOptions = useMemo(() =>
    products.map((p) => ({
      value: p.id,
      label: `${p.fullCode} — ${p.name}`,
      description: p.shortDescription ?? undefined,
      keywords: p.shortDescription ? [p.shortDescription] : undefined,
    })),
    [products]
  );

  // Get supplier products for pricing hints
  const { data: supplierProductsData } = trpc.tradingSupplierProduct.list.useQuery(
    { supplierId: selectedSupplierId },
    { enabled: !!selectedSupplierId }
  );

  const supplierProducts = supplierProductsData?.supplierProducts ?? [];

  // Create mutation
  const createMutation = trpc.tradingPurchaseOrder.create.useMutation({
    onSuccess: (data) => {
      showSuccessToast(t('common.created'));
      navigate(`/trading/purchasing/${data.id}`);
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  // Calculate totals
  let subtotal = 0;
  let taxAmount = 0;
  watchedValues.items?.forEach((item) => {
    const lineSubtotal = (item.quantity || 0) * (item.unitPrice || 0);
    const lineTax = lineSubtotal * ((item.taxRate || 0) / 100);
    subtotal += lineSubtotal;
    taxAmount += lineTax;
  });
  const total = subtotal + taxAmount;

  const handleAddEmptyLine = () => {
    appendItem({
      productId: '',
      productName: '',
      productCode: '',
      productDescription: '',
      quantity: 1,
      unitPrice: 0,
      taxRate: 0,
      notes: '',
    });
  };

  const handleProductSelect = (index: number, productId: string) => {
    const product = products.find((p) => p.id === productId);
    if (!product) return;

    // Get supplier price if available
    const supplierProduct = supplierProducts.find((sp: SupplierProduct) => sp.productId === productId);
    const unitPrice = supplierProduct?.unitCost ?? 0;

    setValue(`items.${index}.productId`, product.id);
    setValue(`items.${index}.productName`, product.name);
    setValue(`items.${index}.productCode`, product.fullCode);
    setValue(`items.${index}.productDescription`, product.shortDescription ?? '');
    setValue(`items.${index}.unitPrice`, unitPrice);
  };

  const onSubmit = (data: PurchaseOrderForm) => {
    if (!data.supplierId) {
      showErrorToast(t('purchasing.validation.selectSupplier'));
      return;
    }

    if (!data.warehouseId) {
      showErrorToast(t('purchasing.validation.selectWarehouse'));
      return;
    }

    if (data.items.length === 0) {
      showErrorToast(t('purchasing.validation.addProduct'));
      return;
    }

    createMutation.mutate({
      supplierId: data.supplierId,
      warehouseId: data.warehouseId,
      orderDate: data.orderDate ? new Date(data.orderDate) : undefined,
      expectedDate: data.expectedDate ? new Date(data.expectedDate) : undefined,
      paymentTerms: data.paymentTerms || undefined,
      deliveryTerms: data.deliveryTerms || undefined,
      notes: data.notes || undefined,
      currency: data.currency,
      invoiceNumber: invoiceNumber.trim() || undefined,
      fileUrl: invoiceFile?.fileUrl || undefined,
      fileKey: invoiceFile?.fileKey || undefined,
      items: data.items.map((item) => ({
        productId: item.productId,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        taxRate: item.taxRate,
        notes: item.notes || undefined,
      })),
    });
  };

  const formatCurrency = (value: number, currency: string = 'EUR') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(value);
  };

  const selectedSupplier = suppliers.find((s) => s.id === selectedSupplierId);
  const selectedWarehouse = warehouses.find((w) => w.id === selectedWarehouseId);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/trading/purchasing"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('common.back')}
          </Link>
          <h1 className="text-2xl font-bold">{t('purchasing.purchaseOrders.addOrder')}</h1>
        </div>
        <Button
          onClick={handleSubmit(onSubmit)}
          disabled={createMutation.isPending}
        >
          {createMutation.isPending && (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          )}
          {t('common.save')}
        </Button>
      </div>

      <form className="grid gap-6 lg:grid-cols-3">
        {/* Main Content */}
        <div className="space-y-6 lg:col-span-2">
          {/* Supplier & Warehouse Section */}
          <div className="border-border border bg-card p-6">
            <h2 className="flex items-center gap-2 font-semibold mb-4">
              <Building2 className="h-5 w-5" />
              {t('purchasing.purchaseOrders.supplier')} & {t('purchasing.purchaseOrders.warehouse')}
            </h2>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>{t('purchasing.purchaseOrders.supplier')} *</Label>
                <Combobox
                  options={suppliers.map((s) => ({
                    value: s.id,
                    label: s.companyName,
                  }))}
                  value={selectedSupplierId || ''}
                  onValueChange={(value) => setValue('supplierId', value)}
                  placeholder={t('purchasing.purchaseOrders.selectSupplier')}
                  searchPlaceholder={t('purchasing.suppliers.searchPlaceholder')}
                />
                {selectedSupplier && (
                  <p className="text-xs text-muted-foreground">
                    {selectedSupplier.contactName} - {selectedSupplier.email}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label>{t('purchasing.purchaseOrders.warehouse')} *</Label>
                <Combobox
                  options={warehouses.map((w) => ({
                    value: w.id,
                    label: `${w.code} - ${w.name}`,
                  }))}
                  value={selectedWarehouseId || ''}
                  onValueChange={(value) => setValue('warehouseId', value)}
                  placeholder={t('inventory.stockLevels.selectWarehouse')}
                  searchPlaceholder={t('warehouses.searchPlaceholder')}
                />
                {selectedWarehouse && (
                  <p className="text-xs text-muted-foreground">
                    {selectedWarehouse.city}, {selectedWarehouse.country}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Invoice Information */}
          <div className="border-border border bg-card p-6">
            <h2 className="flex items-center gap-2 font-semibold mb-4">
              <FileText className="h-5 w-5" />
              {t('purchasing.purchaseOrders.invoiceInfo')}
            </h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="invoiceNumber">{t('purchasing.purchaseOrders.invoiceNumber')}</Label>
                <Input
                  id="invoiceNumber"
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  placeholder={t('purchasing.purchaseOrders.invoiceNumberPlaceholder')}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('purchasing.purchaseOrders.invoiceFile')}</Label>
                <FileUpload
                  folder="supplier-invoices"
                  accept=".pdf,.jpg,.jpeg,.png"
                  maxSizeMB={10}
                  onUploaded={(result) => setInvoiceFile({ fileUrl: result.fileUrl, fileKey: result.fileKey })}
                  onRemoved={() => setInvoiceFile(null)}
                />
              </div>
            </div>
          </div>

          {/* Order Details */}
          <div className="border-border border bg-card p-6">
            <h2 className="flex items-center gap-2 font-semibold mb-4">
              <Calendar className="h-5 w-5" />
              {t('purchasing.purchaseOrders.orderDate')} & {t('purchasing.purchaseOrders.expectedDate')}
            </h2>

            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="orderDate">{t('purchasing.purchaseOrders.orderDate')} *</Label>
                <Input
                  type="date"
                  id="orderDate"
                  {...register('orderDate', { required: true })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="expectedDate">{t('purchasing.purchaseOrders.expectedDate')}</Label>
                <Input
                  type="date"
                  id="expectedDate"
                  {...register('expectedDate')}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('purchasing.purchaseOrders.currency')}</Label>
                <Combobox
                  options={[
                    { value: 'EUR', label: 'EUR' },
                    { value: 'USD', label: 'USD' },
                    { value: 'GBP', label: 'GBP' },
                    { value: 'TRY', label: 'TRY' },
                  ]}
                  value={watchedValues.currency}
                  onValueChange={(value) => setValue('currency', value)}
                  placeholder={t('purchasing.purchaseOrders.currency')}
                  searchPlaceholder="Search..."
                />
              </div>
            </div>
          </div>

          {/* Order Items */}
          <div className="border-border border bg-card">
            {/* Header */}
            <div className="p-4 border-b border-border">
              <h2 className="flex items-center gap-2 font-semibold">
                <Package className="h-5 w-5" />
                {t('purchasing.purchaseOrders.items')} ({items.length})
              </h2>
            </div>

            {/* Items list — no scroll container, grows naturally */}
            {items.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <ShoppingCart className="h-9 w-9 text-muted-foreground/30 mb-2" />
                <p className="text-sm text-muted-foreground mb-3">
                  {!selectedSupplierId
                    ? t('purchasing.purchaseOrders.selectSupplierFirst')
                    : t('purchasing.purchaseOrders.noOrders')}
                </p>
                {selectedSupplierId && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleAddEmptyLine}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    {t('purchasing.purchaseOrders.addNewLine')}
                  </Button>
                )}
              </div>
            ) : (
              <>
                {/* Table header (desktop) */}
                <div className="hidden sm:grid grid-cols-[28px_1fr_80px_120px_60px_110px_36px] gap-2 px-4 py-2 bg-muted/50 border-b border-border text-xs font-medium text-muted-foreground">
                  <span>#</span>
                  <span>{t('purchasing.purchaseOrders.product')}</span>
                  <span className="text-right">{t('purchasing.goodsReceipts.orderedQty')}</span>
                  <span className="text-right">{t('purchasing.supplierProducts.unitCost')}</span>
                  <span className="text-right">{t('purchasing.purchaseOrders.tax')}%</span>
                  <span className="text-right">{t('purchasing.purchaseOrders.lineTotal')}</span>
                  <span></span>
                </div>

                {/* Item rows — no max-height, flows with the page */}
                <div className="divide-y divide-border">
                  {items.map((field, index) => {
                    const item = watchedValues.items[index];
                    const lineSubtotal = (item?.quantity || 0) * (item?.unitPrice || 0);
                    const lineTax = lineSubtotal * ((item?.taxRate || 0) / 100);
                    const lineTotal = lineSubtotal + lineTax;

                    return (
                      <div
                        key={field.id}
                        className="group grid gap-2 px-4 py-3 sm:grid-cols-[28px_1fr_80px_120px_60px_110px_36px] sm:items-start sm:py-2 hover:bg-muted/20 transition-colors"
                      >
                        {/* Row number */}
                        <span className="hidden sm:flex items-center text-xs text-muted-foreground tabular-nums h-8">{index + 1}</span>

                        {/* Product selector */}
                        <div className="min-w-0">
                          <Label className="text-xs text-muted-foreground sm:hidden mb-1 block">{t('purchasing.purchaseOrders.product')}</Label>
                          <Combobox
                            options={productOptions}
                            value={item?.productId || ''}
                            onValueChange={(value) => handleProductSelect(index, value)}
                            placeholder={t('purchasing.purchaseOrders.selectProduct')}
                            searchPlaceholder={t('products.searchPlaceholder')}
                          />
                          {item?.productDescription && (
                            <p className="text-xs text-muted-foreground mt-0.5 truncate" title={item.productDescription}>
                              {item.productDescription}
                            </p>
                          )}
                        </div>

                        {/* Quantity */}
                        <div>
                          <Label className="text-xs text-muted-foreground sm:hidden mb-1 block">{t('purchasing.goodsReceipts.orderedQty')}</Label>
                          <Input
                            type="number"
                            {...register(`items.${index}.quantity`, { valueAsNumber: true })}
                            min="1"
                            className="h-8 text-sm text-right tabular-nums"
                          />
                        </div>

                        {/* Unit price */}
                        <div>
                          <Label className="text-xs text-muted-foreground sm:hidden mb-1 block">{t('purchasing.supplierProducts.unitCost')}</Label>
                          <Input
                            type="number"
                            {...register(`items.${index}.unitPrice`, { valueAsNumber: true })}
                            step="0.01"
                            min="0"
                            className="h-8 text-sm text-right tabular-nums"
                          />
                        </div>

                        {/* Tax % */}
                        <div>
                          <Label className="text-xs text-muted-foreground sm:hidden mb-1 block">{t('purchasing.purchaseOrders.tax')}%</Label>
                          <Input
                            type="number"
                            {...register(`items.${index}.taxRate`, { valueAsNumber: true })}
                            step="0.01"
                            min="0"
                            max="100"
                            className="h-8 text-sm text-right tabular-nums"
                          />
                        </div>

                        {/* Line total */}
                        <div className="text-right flex items-center justify-end h-8">
                          <Label className="text-xs text-muted-foreground sm:hidden mr-2">{t('purchasing.purchaseOrders.lineTotal')}</Label>
                          <span className="text-sm font-medium tabular-nums">
                            {formatCurrency(lineTotal, watchedValues.currency)}
                          </span>
                        </div>

                        {/* Delete */}
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          className="justify-self-center p-1 rounded text-muted-foreground opacity-0 group-hover:opacity-100 focus:opacity-100 hover:text-destructive hover:bg-destructive/10 transition-all h-8 flex items-center"
                          aria-label={`${tCommon('delete')} - ${item?.productName || ''}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>

                {/* Add line button */}
                <div className="border-t border-border px-4 py-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleAddEmptyLine}
                    className="w-full text-muted-foreground hover:text-foreground"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    {t('purchasing.purchaseOrders.addNewLine')}
                  </Button>
                </div>

                {/* Totals footer */}
                <div className="border-t border-border bg-muted/30 px-4 py-3">
                  <div className="flex flex-col items-end gap-1 text-sm">
                    <div className="flex items-center gap-8">
                      <span className="text-muted-foreground">{t('purchasing.purchaseOrders.subtotal')}</span>
                      <span className="tabular-nums w-[110px] text-right">{formatCurrency(subtotal, watchedValues.currency)}</span>
                    </div>
                    <div className="flex items-center gap-8">
                      <span className="text-muted-foreground">{t('purchasing.purchaseOrders.tax')}</span>
                      <span className="tabular-nums w-[110px] text-right">{formatCurrency(taxAmount, watchedValues.currency)}</span>
                    </div>
                    <div className="flex items-center gap-8 pt-1 border-t border-border font-semibold">
                      <span>{t('purchasing.purchaseOrders.total')}</span>
                      <span className="tabular-nums w-[110px] text-right">{formatCurrency(total, watchedValues.currency)}</span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Terms & Notes */}
          <div className="border-border border bg-card p-6">
            <h2 className="flex items-center gap-2 font-semibold mb-4">
              <FileText className="h-5 w-5" />
              {t('purchasing.purchaseOrders.notes')}
            </h2>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="paymentTerms">{t('purchasing.purchaseOrders.paymentTerms')}</Label>
                <Input
                  id="paymentTerms"
                  {...register('paymentTerms')}
                  placeholder={t('purchasing.purchaseOrders.paymentTermsPlaceholder')}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="deliveryTerms">{t('purchasing.purchaseOrders.deliveryTerms')}</Label>
                <Input
                  id="deliveryTerms"
                  {...register('deliveryTerms')}
                  placeholder={t('purchasing.purchaseOrders.deliveryTermsPlaceholder')}
                />
              </div>
            </div>

            <div className="mt-4 space-y-2">
              <Label htmlFor="notes">{t('purchasing.purchaseOrders.notes')}</Label>
              <Textarea
                id="notes"
                {...register('notes')}
                placeholder={t('purchasing.purchaseOrders.notesPlaceholder')}
                rows={3}
              />
            </div>
          </div>
        </div>

        {/* Sidebar - Order Summary */}
        <div className="space-y-6">
          <div className="border-border border bg-card p-6 sticky top-6">
            <h2 className="flex items-center gap-2 font-semibold mb-4">
              <Calculator className="h-5 w-5" />
              {t('purchasing.purchaseOrders.summary')}
            </h2>

            <div className="space-y-3 text-sm">
              {selectedSupplier && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('purchasing.purchaseOrders.supplier')}</span>
                  <span className="font-medium">{selectedSupplier.companyName}</span>
                </div>
              )}

              {selectedWarehouse && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('purchasing.purchaseOrders.warehouse')}</span>
                  <span className="font-medium">{selectedWarehouse.code}</span>
                </div>
              )}

              {watchedValues.orderDate && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('purchasing.purchaseOrders.orderDate')}</span>
                  <span className="font-medium">
                    {format(new Date(watchedValues.orderDate), 'MMM d, yyyy')}
                  </span>
                </div>
              )}

              {watchedValues.expectedDate && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('purchasing.purchaseOrders.expectedDate')}</span>
                  <span className="font-medium">
                    {format(new Date(watchedValues.expectedDate), 'MMM d, yyyy')}
                  </span>
                </div>
              )}

              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('purchasing.purchaseOrders.items')}</span>
                <span className="font-medium">{items.length}</span>
              </div>

              <div className="border-t my-3" />

              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('purchasing.purchaseOrders.subtotal')}</span>
                <span>{formatCurrency(subtotal, watchedValues.currency)}</span>
              </div>

              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('purchasing.purchaseOrders.tax')}</span>
                <span>{formatCurrency(taxAmount, watchedValues.currency)}</span>
              </div>

              <div className="flex justify-between text-base pt-2 border-t">
                <span className="font-semibold">{t('purchasing.purchaseOrders.total')}</span>
                <span className="font-bold">
                  {formatCurrency(total, watchedValues.currency)}
                </span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-6 space-y-2">
              <Button
                type="button"
                className="w-full"
                onClick={handleSubmit(onSubmit)}
                disabled={createMutation.isPending || items.length === 0}
              >
                {createMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {t('purchasing.purchaseOrders.createOrder')}
              </Button>
            </div>

            {/* Validation Warnings */}
            {(!selectedSupplierId || !selectedWarehouseId || items.length === 0) && (
              <div className="mt-4 space-y-2">
                {!selectedSupplierId && (
                  <p className="text-xs text-yellow-600">{t('purchasing.validation.selectSupplierToContinue')}</p>
                )}
                {!selectedWarehouseId && (
                  <p className="text-xs text-yellow-600">{t('purchasing.validation.selectWarehouseToContinue')}</p>
                )}
                {items.length === 0 && (
                  <p className="text-xs text-yellow-600">{t('purchasing.validation.addAtLeastOneProduct')}</p>
                )}
              </div>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}
